import {
  ArrowLeft,
  LoaderCircle,
  Save,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import ProveedorModal from '../proveedores/ProveedorModal'
import { useEmpresa } from '../../context/EmpresaContext'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import {
  categoriasPermitidas,
  mensajeErrorGasto,
  puedeModificarGasto,
  soloCamposPago,
  tipoDocumentoInicial,
  tiposDocumentoPermitidos,
} from '../../utils/permisosGastos'
import { formatearRut } from '../../utils/rut'
import ModalMensaje from '../ui/ModalMensaje'

const OPCION_CREAR_PROVEEDOR = '__crear_proveedor__'

function obtenerFechaActual() {
  const fecha = new Date()
  const año = fecha.getFullYear()
  const mes = String(fecha.getMonth() + 1).padStart(2, '0')
  const dia = String(fecha.getDate()).padStart(2, '0')

  return `${año}-${mes}-${dia}`
}

function convertirNumero(valor) {
  const numero = Number.parseFloat(valor)

  return Number.isFinite(numero) ? numero : 0
}

function formatearMonto(valor) {
  return convertirNumero(valor).toLocaleString('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  })
}

function obtenerValoresIniciales(gastoInicial, rol) {
  return {
    fecha_gasto:
      gastoInicial?.fecha_gasto || obtenerFechaActual(),

    fecha_vencimiento:
      gastoInicial?.fecha_vencimiento || '',

    fecha_pago:
      gastoInicial?.fecha_pago || '',

    categoria_id:
      gastoInicial?.categoria_id || '',

    proveedor_id:
      gastoInicial?.proveedor_id || '',

    tipo_documento:
      gastoInicial?.tipo_documento || tipoDocumentoInicial(rol),

    numero_documento:
      gastoInicial?.numero_documento || '',

    descripcion:
      gastoInicial?.descripcion || '',

    monto_neto:
      gastoInicial?.monto_neto?.toString() || '',

    monto_iva:
      gastoInicial?.monto_iva?.toString() || '',

    monto_exento:
      gastoInicial?.monto_exento?.toString() || '',

    otros_impuestos:
      gastoInicial?.otros_impuestos?.toString() || '',

    medio_pago:
      gastoInicial?.medio_pago || '',

    estado_pago:
      gastoInicial?.estado_pago || 'pendiente',

    observaciones:
      gastoInicial?.observaciones || '',
  }
}

function FormularioGasto({
  modo = 'crear',
  gastoInicial = null,
  facturaVinculada = null,
  cargandoInicial = false,
  onGuardar,
  onCancelar,
}) {
  const { empresaActual } = useEmpresa()
  const {
    modal: modalFormulario,
    abrirModal: abrirModalFormulario,
  } = useModalMensaje()

  const esEdicion = modo === 'editar'
  const rol = empresaActual?.rol

  // Gasto generado por una factura de Abastecimiento: el backend protege
  // importes, categoría, proveedor, fechas y documento.
  const bloqueado = Boolean(facturaVinculada)

  const [categorias, setCategorias] = useState([])
  const [proveedores, setProveedores] = useState([])
  const [cargandoOpciones, setCargandoOpciones] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [modalProveedorAbierto, setModalProveedorAbierto] =
    useState(false)

  const [formulario, setFormulario] = useState(() =>
    obtenerValoresIniciales(gastoInicial, rol)
  )

  const idCargadoRef = useRef(gastoInicial?.id || null)

  useEffect(() => {
    if (!gastoInicial) return
    if (idCargadoRef.current === gastoInicial.id) return

    idCargadoRef.current = gastoInicial.id
    setFormulario(obtenerValoresIniciales(gastoInicial, rol))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gastoInicial])

  const cargarProveedores = async () => {
    if (!empresaActual?.id) return []

    const { data, error: errorProveedores } = await supabase
      .from('proveedores')
      .select('id, razon_social, rut')
      .eq('empresa_id', empresaActual.id)
      .eq('activo', true)
      .order('razon_social', { ascending: true })

    if (errorProveedores) {
      throw errorProveedores
    }

    const listado = data || []
    setProveedores(listado)

    return listado
  }

  useEffect(() => {
    const cargarOpciones = async () => {
      if (!empresaActual?.id) {
        return
      }

      try {
        setCargandoOpciones(true)

        const [respuestaCategorias] = await Promise.all([
          supabase
            .from('categorias_gasto')
            .select('id, nombre')
            .eq('empresa_id', empresaActual.id)
            .eq('activo', true)
            .order('nombre'),

          cargarProveedores(),
        ])

        if (respuestaCategorias.error) {
          throw respuestaCategorias.error
        }

        setCategorias(respuestaCategorias.data || [])
      } catch (errorCarga) {
        console.error('Error al cargar datos:', errorCarga)

        abrirModalFormulario({
          tipo: 'error',
          titulo: 'Error al cargar datos',
          mensaje:
            errorCarga.message ||
            'No fue posible cargar las categorías y proveedores.',
        })
      } finally {
        setCargandoOpciones(false)
      }
    }

    cargarOpciones()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  const montoTotal = useMemo(() => {
    return (
      convertirNumero(formulario.monto_neto) +
      convertirNumero(formulario.monto_iva) +
      convertirNumero(formulario.monto_exento) +
      convertirNumero(formulario.otros_impuestos)
    )
  }, [
    formulario.monto_neto,
    formulario.monto_iva,
    formulario.monto_exento,
    formulario.otros_impuestos,
  ])

  const actualizarCampo = (event) => {
    const { name, value } = event.target

    setFormulario((formularioActual) => ({
      ...formularioActual,
      [name]: value,
    }))
  }

  const cambiarProveedor = (event) => {
    const { value } = event.target

    if (value === OPCION_CREAR_PROVEEDOR) {
      setModalProveedorAbierto(true)
      return
    }

    setFormulario((formularioActual) => ({
      ...formularioActual,
      proveedor_id: value,
    }))
  }

  const cerrarModalProveedor = () => {
    setModalProveedorAbierto(false)
  }

  const alGuardarProveedor = async (proveedorGuardado) => {
    setModalProveedorAbierto(false)

    try {
      await cargarProveedores()
    } catch (errorRecarga) {
      console.error(
        'Error al recargar proveedores:',
        errorRecarga
      )
    }

    if (proveedorGuardado?.id) {
      setFormulario((formularioActual) => ({
        ...formularioActual,
        proveedor_id: proveedorGuardado.id,
      }))
    }
  }

  const manejarEnvio = async (event) => {
    event.preventDefault()

    if (!formulario.categoria_id) {
      abrirModalFormulario({
        tipo: 'error',
        titulo: 'Categoría requerida',
        mensaje: 'Debes seleccionar una categoría.',
      })
      return
    }

    const categoriaElegida = categorias.find(
      (categoria) => categoria.id === formulario.categoria_id
    )

    if (
      !bloqueado &&
      !puedeModificarGasto(rol, {
        tipoDocumento: formulario.tipo_documento,
        categoria: categoriaElegida,
      })
    ) {
      abrirModalFormulario({
        tipo: 'error',
        titulo: 'Operación no permitida',
        mensaje:
          'Tu perfil no puede registrar este gasto: las facturas manuales requieren Administrador o Aprobador, y la categoría «Compras de Fruta» solo el Administrador.',
      })
      return
    }

    if (!formulario.descripcion.trim()) {
      abrirModalFormulario({
        tipo: 'error',
        titulo: 'Descripción requerida',
        mensaje: 'Debes ingresar una descripción.',
      })
      return
    }

    if (montoTotal <= 0) {
      abrirModalFormulario({
        tipo: 'error',
        titulo: 'Monto inválido',
        mensaje: 'El monto total debe ser mayor que cero.',
      })
      return
    }

    const datosNormalizados = {
      categoria_id: formulario.categoria_id,

      proveedor_id:
        formulario.proveedor_id || null,

      fecha_gasto:
        formulario.fecha_gasto,

      fecha_vencimiento:
        formulario.fecha_vencimiento || null,

      fecha_pago:
        formulario.fecha_pago || null,

      tipo_documento:
        formulario.tipo_documento,

      numero_documento:
        formulario.numero_documento.trim() || null,

      descripcion:
        formulario.descripcion.trim(),

      monto_neto:
        convertirNumero(formulario.monto_neto),

      monto_iva:
        convertirNumero(formulario.monto_iva),

      monto_exento:
        convertirNumero(formulario.monto_exento),

      otros_impuestos:
        convertirNumero(formulario.otros_impuestos),

      monto_total:
        montoTotal,

      medio_pago:
        formulario.medio_pago || null,

      estado_pago:
        formulario.estado_pago,

      observaciones:
        formulario.observaciones.trim() || null,
    }

    try {
      setGuardando(true)
      await onGuardar(
        bloqueado ? soloCamposPago(datosNormalizados) : datosNormalizados
      )
    } catch (errorGuardado) {
      console.error('Error al guardar gasto:', errorGuardado)

      abrirModalFormulario({
        tipo: 'error',
        titulo: 'No se pudo guardar',
        mensaje: mensajeErrorGasto(errorGuardado),
      })
    } finally {
      setGuardando(false)
    }
  }

  if (cargandoInicial || cargandoOpciones) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <LoaderCircle
            size={38}
            className="mx-auto animate-spin text-emerald-600"
          />

          <p className="mt-4 font-medium text-slate-500">
            Cargando formulario...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={onCancelar}
        className="mb-5 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
      >
        <ArrowLeft size={18} />
        Volver a gastos
      </button>

      <div className="mb-8">
        <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
          {empresaActual?.nombre_fantasia}
        </p>

        <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
          {esEdicion ? 'Editar gasto' : 'Nuevo gasto'}
        </h2>

        <p className="mt-2 text-slate-500">
          {esEdicion && gastoInicial?.numero_documento
            ? `Documento N.º ${gastoInicial.numero_documento}`
            : esEdicion
              ? 'Actualiza los antecedentes del gasto.'
              : 'Ingresa los antecedentes del gasto.'}
        </p>
      </div>

      {bloqueado && (
        <div className="mb-6 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-800">
          <p className="font-semibold">
            Gasto generado desde Abastecimiento
          </p>

          <p className="mt-1">
            Corresponde a la factura N.º{' '}
            {facturaVinculada.numero_documento}. Sus importes, categoría,
            proveedor, fechas y documento no se pueden modificar. Solo
            puedes actualizar el pago, la descripción y las observaciones.
          </p>
        </div>
      )}

      <form
        onSubmit={manejarEnvio}
        className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          <Campo label="Fecha del gasto" requerido>
            <input
              type="date"
              name="fecha_gasto"
              disabled={bloqueado}
              value={formulario.fecha_gasto}
              onChange={actualizarCampo}
              required
              className={estiloInput}
            />
          </Campo>

          <Campo label="Categoría" requerido>
            <select
              name="categoria_id"
              disabled={bloqueado}
              value={formulario.categoria_id}
              onChange={actualizarCampo}
              required
              className={estiloInput}
            >
              <option value="">Seleccionar categoría</option>

              {categoriasPermitidas(rol, categorias).map((categoria) => (
                <option
                  key={categoria.id}
                  value={categoria.id}
                >
                  {categoria.nombre}
                </option>
              ))}
            </select>
          </Campo>

          <Campo label="Proveedor">
            <select
              name="proveedor_id"
              disabled={bloqueado}
              value={formulario.proveedor_id}
              onChange={cambiarProveedor}
              className={estiloInput}
            >
              <option value="">Sin proveedor</option>

              {proveedores.map((proveedor) => (
                <option
                  key={proveedor.id}
                  value={proveedor.id}
                >
                  {proveedor.razon_social}
                  {proveedor.rut
                    ? ` — ${formatearRut(proveedor.rut)}`
                    : ''}
                </option>
              ))}

              <option value={OPCION_CREAR_PROVEEDOR}>
                + Crear nuevo proveedor
              </option>
            </select>
          </Campo>

          <Campo label="Tipo de documento">
            <select
              name="tipo_documento"
              disabled={bloqueado}
              value={formulario.tipo_documento}
              onChange={actualizarCampo}
              className={estiloInput}
            >
              {tiposDocumentoPermitidos(rol).map(([valor, etiqueta]) => (
                <option
                  key={valor}
                  value={valor}
                >
                  {etiqueta}
                </option>
              ))}
            </select>
          </Campo>

          <Campo label="Número de documento">
            <input
              type="text"
              name="numero_documento"
              disabled={bloqueado}
              value={formulario.numero_documento}
              onChange={actualizarCampo}
              placeholder="Ejemplo: 1258"
              className={estiloInput}
            />
          </Campo>

          <Campo label="Fecha de vencimiento">
            <input
              type="date"
              name="fecha_vencimiento"
              disabled={bloqueado}
              value={formulario.fecha_vencimiento}
              onChange={actualizarCampo}
              className={estiloInput}
            />
          </Campo>

          <div className="md:col-span-2 xl:col-span-3">
            <Campo label="Descripción" requerido>
              <input
                type="text"
                name="descripcion"
                value={formulario.descripcion}
                onChange={actualizarCampo}
                placeholder="Describe el gasto realizado"
                required
                className={estiloInput}
              />
            </Campo>
          </div>
        </div>

        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Montos
          </h3>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-5">
            <Campo label="Monto neto">
              <input
                type="number"
                name="monto_neto"
              disabled={bloqueado}
                value={formulario.monto_neto}
                onChange={actualizarCampo}
                min="0"
                step="0.01"
                placeholder="0"
                className={estiloInput}
              />
            </Campo>

            <Campo label="IVA">
              <input
                type="number"
                name="monto_iva"
              disabled={bloqueado}
                value={formulario.monto_iva}
                onChange={actualizarCampo}
                min="0"
                step="0.01"
                placeholder="0"
                className={estiloInput}
              />
            </Campo>

            <Campo label="Monto exento">
              <input
                type="number"
                name="monto_exento"
              disabled={bloqueado}
                value={formulario.monto_exento}
                onChange={actualizarCampo}
                min="0"
                step="0.01"
                placeholder="0"
                className={estiloInput}
              />
            </Campo>

            <Campo label="Otros impuestos">
              <input
                type="number"
                name="otros_impuestos"
              disabled={bloqueado}
                value={formulario.otros_impuestos}
                onChange={actualizarCampo}
                min="0"
                step="0.01"
                placeholder="0"
                className={estiloInput}
              />
            </Campo>

            <Campo label="Monto total">
              <input
                type="text"
                value={formatearMonto(montoTotal)}
                readOnly
                className={`${estiloInput} cursor-not-allowed bg-slate-100 font-bold text-slate-900`}
              />
            </Campo>
          </div>
        </div>

        <div className="mt-8 border-t border-slate-200 pt-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            <Campo label="Estado del pago">
              <select
                name="estado_pago"
                value={formulario.estado_pago}
                onChange={actualizarCampo}
                className={estiloInput}
              >
                <option value="pendiente">Pendiente</option>
                <option value="parcial">Pago parcial</option>
                <option value="pagado">Pagado</option>
              </select>
            </Campo>

            <Campo label="Medio de pago">
              <select
                name="medio_pago"
                value={formulario.medio_pago}
                onChange={actualizarCampo}
                className={estiloInput}
              >
                <option value="">Sin especificar</option>
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">
                  Transferencia
                </option>
                <option value="tarjeta_credito">
                  Tarjeta de crédito
                </option>
                <option value="tarjeta_debito">
                  Tarjeta de débito
                </option>
                <option value="cheque">Cheque</option>
                <option value="otro">Otro</option>
              </select>
            </Campo>

            <Campo label="Fecha de pago">
              <input
                type="date"
                name="fecha_pago"
                value={formulario.fecha_pago}
                onChange={actualizarCampo}
                className={estiloInput}
              />
            </Campo>

            <div className="md:col-span-2 xl:col-span-3">
              <Campo label="Observaciones">
                <textarea
                  name="observaciones"
                  value={formulario.observaciones}
                  onChange={actualizarCampo}
                  rows={4}
                  placeholder="Información adicional del gasto"
                  className={estiloInput}
                />
              </Campo>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-slate-500">
              Total del gasto
            </p>

            <p className="text-3xl font-bold text-slate-900">
              {formatearMonto(montoTotal)}
            </p>
          </div>

          <div className="flex flex-col-reverse gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onCancelar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 hover:bg-slate-100 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando ? (
                <LoaderCircle
                  size={20}
                  className="animate-spin"
                />
              ) : (
                <Save size={20} />
              )}

              {guardando
                ? 'Guardando...'
                : esEdicion
                  ? 'Guardar cambios'
                  : 'Guardar gasto'}
            </button>
          </div>
        </div>
      </form>

      {modalProveedorAbierto && (
        <ProveedorModal
          proveedor={null}
          onClose={cerrarModalProveedor}
          onGuardado={alGuardarProveedor}
        />
      )}

      <ModalMensaje {...modalFormulario} />
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-3 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500'

function Campo({ label, requerido = false, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}

        {requerido && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      {children}
    </div>
  )
}

export default FormularioGasto
